import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'

export type Lang = 'en' | 'zh'
const LANGS: readonly Lang[] = ['en', 'zh']
export const DEFAULT_LANG: Lang = 'en'

export function isLang(value: string | null): value is Lang {
  return value !== null && (LANGS as readonly string[]).includes(value)
}

/**
 * The learner's active language.
 *
 * `?lang=` on the URL wins, because it is what a deep link, a refresh and the
 * boot-time i18next init (`src/i18n.ts`) all agree on.
 *
 * It falls back to the live i18next language rather than straight to English:
 * none of the ~96 `navigate('/path')` calls in `src/screens` preserve a query
 * string, so between a learner tapping a real control and the player putting
 * the run back on the URL there are renders where `?lang=` simply is not there.
 * Resolving those to `DEFAULT_LANG` is what made a Chinese lesson snap back to
 * English on the first tap. `LangToggle` sets both, so the singleton is an
 * accurate record of the choice — and subscribing to it here re-renders the
 * readers when the toggle flips.
 */
export function useLang(): Lang {
  const [params] = useSearchParams()
  const { i18n } = useTranslation()
  const raw = params.get('lang')
  if (isLang(raw)) return raw
  return isLang(i18n.language) ? i18n.language : DEFAULT_LANG
}
